import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { toast } from 'sonner';
import { todayISO } from '@/lib/format';
import { Loader2, ArrowRight } from 'lucide-react';

const ENDPOINTS = ['Rekening', 'Agi', 'Albi'];

const DEFAULT = {
  date: todayISO(),
  from_cashier: 'Rekening',
  to_cashier: 'Agi',
  amount: '',
  payment_method: 'Transfer',
  note: '',
};

export default function CashMovementDialog({ open, onOpenChange, pid, initial, onSaved }) {
  const [form, setForm] = useState(DEFAULT);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial;

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          date: (initial.date || todayISO()).substring(0, 10),
          from_cashier: initial.from_cashier || 'Rekening',
          to_cashier: initial.to_cashier || 'Agi',
          amount: initial.amount || '',
          payment_method: initial.payment_method || 'Transfer',
          note: initial.note || '',
        });
      } else setForm(DEFAULT);
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (form.from_cashier === form.to_cashier) { toast.error('Sumber dan tujuan tidak boleh sama'); return; }
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error('Nominal harus lebih dari 0'); return; }
    setSaving(true);
    try {
      const payload = { ...form, amount: amt };
      if (isEdit) await api.put(`/cash-movements/${initial.id}`, payload);
      else await api.post(`/projects/${pid}/cash-movements`, payload);
      toast.success(isEdit ? 'Mutasi diperbarui' : 'Mutasi tersimpan');
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="cash-movement-dialog" aria-describedby="mv-desc" className="max-w-lg bg-[var(--fpccs-surface-1)] border-[var(--fpccs-border)]">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Catatan Transaksi' : 'Catatan Transaksi (Mutasi Dana)'}</DialogTitle>
          <p id="mv-desc" className="sr-only">Form untuk mencatat perpindahan dana antar kasir.</p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-fpccs">Tanggal *</Label>
            <Input type="date" required data-testid="mv-date" value={form.date} onChange={(e) => set('date', e.target.value)} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-end">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-fpccs">Dari *</Label>
              <Select value={form.from_cashier} onValueChange={(v) => set('from_cashier', v)}>
                <SelectTrigger data-testid="mv-from"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ENDPOINTS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="hidden sm:flex items-center justify-center pb-2 text-[var(--fpccs-primary)]">
              <ArrowRight className="w-4 h-4" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-fpccs">Ke *</Label>
              <Select value={form.to_cashier} onValueChange={(v) => set('to_cashier', v)}>
                <SelectTrigger data-testid="mv-to"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ENDPOINTS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-fpccs">Nominal (Rp) *</Label>
            <Input type="number" min="0" required autoFocus data-testid="mv-amount" value={form.amount} onChange={(e) => set('amount', e.target.value)} placeholder="cth: 50000000" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-fpccs">Metode</Label>
            <Select value={form.payment_method} onValueChange={(v) => set('payment_method', v)}>
              <SelectTrigger data-testid="mv-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Cash">Cash</SelectItem>
                <SelectItem value="Transfer">Transfer</SelectItem>
                <SelectItem value="QRIS">QRIS</SelectItem>
                <SelectItem value="Lainnya">Lainnya</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-fpccs">Keterangan</Label>
            <Textarea rows={2} data-testid="mv-note" value={form.note} onChange={(e) => set('note', e.target.value)} placeholder="cth: split termin 1 ke albi 50%" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
            <Button type="submit" disabled={saving} data-testid="btn-save-movement" className="bg-[var(--fpccs-primary)] hover:bg-[var(--fpccs-primary-2)] text-[var(--fpccs-bg)]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Simpan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
